import React, { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { AIChatView } from '../../components/ai/AIChatView';
import { useEditLastMessage } from '../../components/ai/hooks/useEditLastMessage';
import type { ChatMessage } from '../../components/ai/types';
import type {
  AIConfig,
  CharacterBook,
  ChatOwnerType,
  PromptSettings,
  SamplerSettings,
} from '../../db/characterTypes';
import { stripFences } from '../core/stripFences';
import { computeAgentContextUsage, usageStatus } from '../hosts/lorebook/contextUsage';
import { AgentReviewModal } from '../review/AgentReviewModal';
import { applyLorebookReview, diffLorebookReview, formatReviewOutcome } from '../review/diff';
import type { LorebookReviewPayload, ReviewDecisions } from '../review/types';
import { AgentChatMessage } from './AgentChatMessage';
import { AgentReviewStatus } from './AgentReviewStatus';
import { AgentToolModeChip } from './AgentToolModeChip';
import { formatAgentBusyLabel } from './busyLabel';
import { LiveSpeech } from './LiveSpeech';
import { LiveThinking } from './LiveThinking';
import { contextMeterHistory, keptEditsOptions } from './loopHistory';
import {
  CONTINUE_MESSAGE,
  interruptedRunMessageIds,
  LOREBOOK_LOOKUP_TOOLS,
  messageNotices,
  shouldRenderAgentMessage,
  visibleToolEvents,
  writeRecapLine,
} from './notices';
import { lorebookMentions } from './mentions';
import { lorebookSuggestions } from './suggestions';
import type { AgentToolTarget } from './types';
import { useLeaveWarning } from './useLeaveWarning';
import { useLorebookAgent, type SetAgentBook } from './useLorebookAgent';

export interface LorebookAgentChatProps {
  aiConfig: AIConfig;
  samplerSettings: SamplerSettings;
  promptSettings: PromptSettings;
  getBook: () => CharacterBook;
  setBook: SetAgentBook;
  getCustomContext: () => Promise<string | null>;
  flushDraft: () => void | Promise<void>;
  takeSnapshot: () => Promise<void>;
  customContextIncluded: boolean;
  customContextCharLength?: number;
  headerLeading?: ReactNode;
  headerActions?: ReactNode;
  onClose?: () => void;
  onRunningChange?: (running: boolean) => void;
  onOpenTarget?: (target: AgentToolTarget) => void;
  requireReview?: boolean;
  keepEdits?: boolean;
  chatOwnerType: ChatOwnerType;
  chatOwnerId: string;
  title?: string;
  emptyBody?: string;
  contextEmptyHint?: string;
  composerHint?: string;
}

export function LorebookAgentChat({
  aiConfig,
  samplerSettings,
  promptSettings,
  getBook,
  setBook,
  getCustomContext,
  flushDraft,
  takeSnapshot,
  customContextIncluded,
  customContextCharLength = 0,
  headerLeading,
  headerActions,
  onClose,
  onRunningChange,
  title = 'Lorebook agent',
  emptyBody = 'Ask it to build or extend this book from custom context. It can add entries directly. Use Snapshots if you need to roll back.',
  contextEmptyHint = 'Custom context is optional. Enable it in the lorebook sidebar to give the agent source notes.',
  composerHint = 'Stop, then Send to retry · Writes go into this book',
  onOpenTarget,
  requireReview = false,
  keepEdits = false,
  chatOwnerType,
  chatOwnerId,
}: LorebookAgentChatProps): React.ReactElement {
  const [review, setReview] = useState<LorebookReviewPayload | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [isApplyingReview, setIsApplyingReview] = useState(false);
  const shouldReview = useCallback(() => requireReview, [requireReview]);
  const holdRunRef = useRef<() => void>(() => undefined);

  const handlePendingReview = useCallback((pending: LorebookReviewPayload) => {
    if (diffLorebookReview(pending).length === 0) return;
    holdRunRef.current();
    setReview(pending);
    setReviewOpen(true);
  }, []);

  const session = useLorebookAgent({
    aiConfig,
    samplerSettings,
    promptSettings,
    getBook,
    setBook,
    getCustomContext,
    flushDraft,
    takeSnapshot,
    shouldReview,
    onPendingReview: handlePendingReview,
    onRunningChange,
    chatOwnerType,
    chatOwnerId,
    keepEditsInContext: keepEdits,
  });
  const { noteReviewOutcome, holdRunForReview, noteRenumberedEntries } = session;
  useEffect(() => {
    holdRunRef.current = holdRunForReview;
  }, [holdRunForReview]);
  useLeaveWarning(session.isProcessing || review != null);

  const handleApplyReview = useCallback(
    async (decisions: ReviewDecisions) => {
      if (!review) return;
      const outcome = formatReviewOutcome(diffLorebookReview(review), decisions);
      const staged = applyLorebookReview(review, decisions);
      if (!staged) {
        noteReviewOutcome(outcome);
        setReview(null);
        setReviewOpen(false);
        return;
      }
      setIsApplyingReview(true);
      try {
        await takeSnapshot();
        const renumbered = new Map<number, number>();
        await setBook((latest) => {
          renumbered.clear();
          return applyLorebookReview(review, decisions, latest, renumbered);
        });
        noteRenumberedEntries(renumbered);
        noteReviewOutcome(formatReviewOutcome(diffLorebookReview(review), decisions, renumbered));
      } finally {
        setIsApplyingReview(false);
        setReview(null);
        setReviewOpen(false);
      }
    },
    [noteRenumberedEntries, noteReviewOutcome, review, setBook, takeSnapshot],
  );

  const handleDiscardReview = useCallback(() => {
    noteReviewOutcome(formatReviewOutcome([], null));
    setReview(null);
    setReviewOpen(false);
  }, [noteReviewOutcome]);

  const handleAskGuarded = useCallback(
    (question: string) => {
      if (review) {
        setReviewOpen(true);
        return Promise.resolve();
      }
      return session.handleAsk(question);
    },
    [review, session],
  );

  const handleContinue = useCallback(() => {
    void handleAskGuarded(CONTINUE_MESSAGE);
  }, [handleAskGuarded]);

  const handleRegenerateGuarded = useCallback(() => {
    if (review) {
      setReviewOpen(true);
      return Promise.resolve();
    }
    return session.handleRegenerate();
  }, [review, session]);

  // The review outcome is noted on the last agent reply, so deleting replies would move it to an earlier run.
  const handleDeleteGuarded = useCallback(
    (messageId: string) => {
      if (review) {
        setReviewOpen(true);
        return;
      }
      session.handleDeleteMessage(messageId);
    },
    [review, session],
  );

  const { lastUserIndex, editingId, startEdit, cancelEdit, commitEdit } = useEditLastMessage(
    session.chatHistory,
    session.handleDeleteMessage,
  );

  const handleEditGuarded = useCallback(
    (message: ChatMessage, setComposerText: (text: string) => void) => {
      if (review) {
        setReviewOpen(true);
        return;
      }
      startEdit(message, setComposerText);
    },
    [review, startEdit],
  );

  const handleComposerAsk = useCallback(
    (question: string) => {
      if (!review) commitEdit();
      return handleAskGuarded(question);
    },
    [commitEdit, handleAskGuarded, review],
  );

  const contextLabels = useMemo(() => {
    const labels = ['Entry catalog'];
    if (customContextIncluded) labels.unshift('Custom context');
    return labels;
  }, [customContextIncluded]);

  // While a live prompt count is pinned, the idle estimate is never shown — skip
  // the catalog rebuild + full-history encode that would otherwise run per commit.
  const contextUsage = useMemo(() => {
    if (session.livePromptTokens != null) {
      const limit = Math.max(1, samplerSettings.contextLength || 0);
      const percentage = Math.min(100, (session.livePromptTokens / limit) * 100);
      return {
        tokens: session.livePromptTokens,
        limit,
        percentage,
        status: usageStatus(percentage),
      };
    }
    return computeAgentContextUsage({
      book: getBook(),
      customContextCharLength,
      customContextIncluded,
      history: contextMeterHistory(
        session.chatHistory,
        session.toolEventsByMessageId,
        LOREBOOK_LOOKUP_TOOLS,
        session.errorByMessageId,
        keptEditsOptions(keepEdits, session.toolMode, samplerSettings),
      ),
      contextLength: samplerSettings.contextLength,
    });
  }, [
    customContextCharLength,
    customContextIncluded,
    getBook,
    keepEdits,
    samplerSettings,
    session.chatHistory,
    session.errorByMessageId,
    session.livePromptTokens,
    session.toolEventsByMessageId,
    session.toolMode,
  ]);

  const lostMessageIds = useMemo(
    () => interruptedRunMessageIds(session.chatHistory, session.toolEventsByMessageId),
    [session.chatHistory, session.toolEventsByMessageId],
  );

  const renderMessage = useCallback(
    (message: ChatMessage, index: number, setComposerText: (text: string) => void) => {
      const events = session.toolEventsByMessageId[message.id] ?? [];
      const notices = messageNotices(session.errorByMessageId[message.id], events);
      const toolEvents = visibleToolEvents(events);
      const writesLost = lostMessageIds.has(message.id);
      const speech = message.role === 'assistant' ? stripFences(message.content) : message.content;
      const recapLine = speech ? null : writeRecapLine(toolEvents, writesLost);
      const showReasoning = aiConfig.showReasoning ?? true;
      if (
        !shouldRenderAgentMessage(
          message.role,
          speech,
          toolEvents,
          notices,
          showReasoning ? message.reasoning ?? '' : '',
        )
      ) {
        return null;
      }
      return (
        <AgentChatMessage
          message={message}
          messageIndex={index}
          chatHistoryLength={session.chatHistory.length}
          isProcessing={session.isProcessing}
          showReasoning={showReasoning}
          showRegenerate
          notices={notices}
          toolEvents={toolEvents}
          writesLost={writesLost}
          recapLine={recapLine}
          onRegenerate={handleRegenerateGuarded}
          onDelete={handleDeleteGuarded}
          onOpenTarget={onOpenTarget}
          onContinue={
            index === session.chatHistory.length - 1 && !session.isProcessing
              ? handleContinue
              : undefined
          }
          onEdit={
            index === lastUserIndex
              ? () => handleEditGuarded(message, setComposerText)
              : undefined
          }
          onCancelEdit={
            message.id === editingId ? () => cancelEdit(setComposerText) : undefined
          }
        />
      );
    },
    [
      aiConfig.showReasoning,
      cancelEdit,
      editingId,
      handleContinue,
      handleDeleteGuarded,
      handleEditGuarded,
      handleRegenerateGuarded,
      lastUserIndex,
      lostMessageIds,
      onOpenTarget,
      session.chatHistory.length,
      session.errorByMessageId,
      session.isProcessing,
      session.toolEventsByMessageId,
    ],
  );

  const reviewChanges = useMemo(
    () => (review ? diffLorebookReview(review) : []),
    [review],
  );
  const hasPendingReview = review != null;

  return (
    <>
    <AIChatView
      title={title}
      emptyTitle={title}
      emptyBody={emptyBody}
      placeholder="Tell the agent what to add…"
      contextLabels={contextLabels}
      contextEmptyHint={contextEmptyHint}
      composerHint={composerHint}
      composerDisabled={hasPendingReview}
      composerDisabledPlaceholder="Pending agent writes. Accept or reject via the yellow Review button…"
      composerDisabledHint="Review pending. Accept or reject via the yellow Review button above"
      headerLeading={headerLeading}
      headerActions={
        <>
          <AgentToolModeChip mode={session.toolMode} />
          <AgentReviewStatus
            requireReview={requireReview}
            pendingCount={review && !reviewOpen ? reviewChanges.length : null}
            onOpen={() => setReviewOpen(true)}
          />
          {headerActions}
        </>
      }
      showReasoning={false}
      showRegenerate
      showStreamDraft={false}
      contextUsage={contextUsage}
      chatHistory={session.chatHistory}
      isProcessing={session.isProcessing}
      error={session.chatHistory.length === 0 ? session.error : null}
      isStreaming={session.isStreaming}
      streamingContent={session.streamingContent}
      streamingReasoning={session.streamingReasoning}
      handleAsk={handleComposerAsk}
      handleRegenerate={handleRegenerateGuarded}
      handleNewChat={session.handleNewChat}
      handleDeleteMessage={handleDeleteGuarded}
      handleAbort={session.handleAbort}
      clearError={session.clearError}
      isHydrating={session.isHydrating}
      hasOlderMessages={session.hasOlderMessages}
      onLoadOlder={session.handleLoadOlder}
      onClose={onClose}
      emptySuggestions={lorebookSuggestions(getBook(), customContextIncluded)}
      mentionOptions={() => lorebookMentions(getBook())}
      renderMessage={renderMessage}
      processingIndicator={
        <div className="py-1 text-fg-muted">
          <div className="flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
            <span className="text-xs">
              {session.busyAction
                ? formatAgentBusyLabel(session.busyAction, getBook().entries)
                : 'Working…'}
            </span>
          </div>
          {aiConfig.showReasoning !== false ? (
            <LiveThinking text={session.streamingReasoning} />
          ) : null}
          <LiveSpeech text={session.streamingContent} isStreaming={session.isStreaming} />
        </div>
      }
    />
    {review && reviewOpen && (
      <AgentReviewModal
        changes={reviewChanges}
        isApplying={isApplyingReview}
        onApply={(decisions) => void handleApplyReview(decisions)}
        onDiscard={handleDiscardReview}
        onMinimize={() => setReviewOpen(false)}
      />
    )}
    </>
  );
}
